<script>
import {computed} from 'vue'
import {Link} from '@inertiajs/vue3'

import {useCartStore} from '@js/store/CartStore.js'
import {useProductStore} from '@js/store/ProductStore.js'
import {useLikeStore} from '@js/store/LikeStore.js'
import {useCompareStore} from '@js/store/CompareStore.js'

import NumberFormatComponent from '@js/components/Formatter/NumberFormatComponent.vue'
import CartQuantityStepper from '@js/components/Sections/Cart/Components/CartQuantityStepper.vue'
import IconHeart from '@js/components/Icons/IconHeart.vue'
import IconHeartFilled from '@js/components/Icons/IconHeartFilled.vue'
import IconScales from '@js/components/Icons/IconScales.vue'
import IconShare from '@js/components/Icons/IconShare.vue'
import IconCartPlus from '@js/components/Icons/IconCartPlus.vue'
import IconCheckMark from '@js/components/Icons/IconCheckMark.vue'
import ProductPriceConfigurator from '@js/components/Sections/Catalog/Components/ProductPriceConfigurator.vue'

export default {
    name: 'ProductCartForm',
    components: {
        ProductPriceConfigurator,
        CartQuantityStepper,
        NumberFormatComponent,
        IconHeart,
        IconHeartFilled,
        IconScales,
        IconShare,
        IconCartPlus,
        IconCheckMark,
        Link,
    },
    props: {
        productId: {type: [Number, String], required: true},
    },
    setup(props) {
        const cartStore = useCartStore()
        const productStore = useProductStore()
        const likeStore = useLikeStore()
        const compareStore = useCompareStore()

        const product = computed(() => {
            return cartStore.productOf(props.productId) || productStore.product || null
        })

        const activePrice = computed(() => cartStore.priceActive(props.productId))
        const currentUnit = computed(() => cartStore.unitLabel(props.productId))
        const hasAnyPrice = computed(() => Number(activePrice.value) > 0)
        const canBuy = computed(() => !!product.value?.available && hasAnyPrice.value)

        const qtyProxy = computed({
            get: () => cartStore.uiOf(props.productId)?.qty ?? 1,
            set: (value) => cartStore.setQtyFromInput(props.productId, value),
        })

        function copyLink() {
            try {
                navigator.clipboard.writeText(window.location.href)
            } catch (_) {}
        }

        return {
            cartStore,
            productStore,
            likeStore,
            compareStore,
            product,
            activePrice,
            currentUnit,
            hasAnyPrice,
            canBuy,
            qtyProxy,
            copyLink,
        }
    },
}
</script>

<template>
    <div v-if="product" class="flex flex-col gap-3">
        <div class="bg-grayBg rounded-2xl p-4 flex flex-col gap-4">
            <div class="flex items-center justify-between">
                <div
                    class="text-white text-xs font-medium px-2 py-1 rounded"
                    :class="[product.available ? 'bg-emerald-600' : 'bg-red-500']"
                >
                    {{ product.available ? 'Товар в наличии' : 'Ожидаем поставку' }}
                </div>

                <div class="flex items-center space-x-3">
                    <IconScales
                        @click.prevent="compareStore.toggleCompare(product.id)"
                        :class="[compareStore.isCompared(product.id) ? 'text-blue-500' : 'text-gray-600']"
                        class="w-5 h-5 cursor-pointer transition"
                    />

                    <IconHeart
                        v-if="!likeStore.isLiked(product.id)"
                        @click.prevent="likeStore.toggleLike(product.id)"
                        class="w-5 h-5 text-gray-600 cursor-pointer transition"
                    />

                    <IconHeartFilled
                        v-else
                        @click.prevent="likeStore.toggleLike(product.id)"
                        class="w-5 h-5 text-red-600 cursor-pointer transition"
                    />

                    <IconShare
                        class="w-4 h-4 text-gray-600 cursor-pointer transition"
                        @click.prevent="copyLink"
                    />
                </div>
            </div>

            <ProductPriceConfigurator
                :key="`pdp-config-${product.id}`"
                :product-id="product.id"
            />

            <template v-if="canBuy">
                <div class="flex justify-between gap-5">
                    <CartQuantityStepper
                        v-model="qtyProxy"
                        :min="cartStore.minQty(product.id)"
                        :step="cartStore.step(product.id)"
                        :disabled="!canBuy"
                        size="sm"
                        width-class="w-32"
                        @inc="cartStore.incQty(product.id)"
                        @dec="cartStore.decQty(product.id)"
                    />
                </div>

                <div class="flex items-center gap-10">
                    <div v-if="hasAnyPrice">
                        <div class="text-xs font-medium">
                            На сумму
                        </div>
                        <div class="text-sm font-semibold text-gray-900">
                            <NumberFormatComponent
                                :number="cartStore.totalPrice(product.id)"
                                append_text=" ₽"
                            />
                        </div>
                    </div>

                    <div v-if="cartStore.weightKg(product.id)">
                        <div class="text-xs font-medium">
                            Вес
                        </div>
                        <div class="text-sm text-gray-900">
                            <NumberFormatComponent
                                :number="cartStore.weightKg(product.id)"
                                append_text=" т"
                                prepend_text="≈ "
                            />
                        </div>
                    </div>

                    <div v-if="cartStore.areaSqM(product.id)">
                        <div class="text-xs font-medium">
                            Площадь
                        </div>
                        <div class="text-sm text-gray-900">
                            <NumberFormatComponent
                                :number="cartStore.areaSqM(product.id)"
                                append_text=" м²"
                                prepend_text="≈ "
                            />
                        </div>
                    </div>
                </div>

                <template v-if="cartStore.isInCartExact(product.id)">
                    <Link
                        :href="route('cart')"
                        class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-base font-semibold text-white transition"
                    >
                        <IconCheckMark class="h-5 w-5"/>
                        Уже в смете
                    </Link>
                </template>

                <template v-else>
                    <button
                        type="button"
                        class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-baseColor text-base font-semibold text-black transition"
                        :disabled="!canBuy"
                        @click="cartStore.addToCartFromCard(product.id)"
                    >
                        <IconCartPlus class="h-5 w-5"/>
                        В смету
                    </button>
                </template>

                <button
                    type="button"
                    class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-black/10 text-base font-semibold text-black transition"
                    @click.prevent="productStore.showFastBuyPopup(product.id)"
                >
                    Купить сейчас
                </button>
            </template>

            <template v-else>
                <button
                    type="button"
                    class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-amber-400 text-base font-semibold text-black transition"
                    @click.prevent="productStore.showFastBuyPopup(product.id)"
                >
                    Узнать цену
                </button>
            </template>
        </div>
    </div>
</template>
